import React, { useState } from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';

import ExpansionPanel from '@material-ui/core/ExpansionPanel';
import ExpansionPanelDetails from '@material-ui/core/ExpansionPanelDetails';
import ExpansionPanelSummary from '@material-ui/core/ExpansionPanelSummary';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';

import Grid from 'LegacyWeployDesignLanguage/Grid';
import Divider from 'LegacyWeployDesignLanguage/Divider';
import formatCurrency from '../../../utils/Currency';

const StyledExpansionPanel = styled(ExpansionPanel)`
  && {
    flex-direction: column;
    position: relative;
    display: flex;
    justify-content: space-between;
    border-radius: ${props => props.theme.borderRadius.medium};
    box-shadow: none;
    border: 1px solid rgba(0, 0, 0, 0.12);
    margin-bottom: ${props => props.theme.spacing[2]};
  };

  &::before {
    height: 0;
  }
`;

const StyledExpansionPanelSummaryTitle = styled.div`
  width: 100%;
`;

const StyledExpansionPanelSummary = styled(ExpansionPanelSummary)`
  padding: 0 ${props => props.theme.spacing[2]};
`;

const StyledExpansionPanelDetails = styled(ExpansionPanelDetails)`
  padding: 0;
`;

const StyledRow = styled.div`
  padding: ${props => props.theme.spacing[2]} 0;
`;

const StyledTitle = styled.div`
  ${props => props.theme.typography.h6};
`;

const StyledSubtitle = styled.div`
  ${props => props.theme.typography.subtitle1};
  color: ${props => props.theme.colors.text.primary};
`;

const StyledSummaryWrapper = styled.div`
  width: 100%;
`;

const StyledDescription = styled.span`
  ${props => props.theme.typography.body1}
  color: ${props => props.theme.colors.text.primary};
`;

const StyledDescriptionGrid = styled(Grid)`
  padding-left: ${props => props.theme.spacing[2]};
`;

const StyledValue = styled.span`
  ${props => props.theme.typography.subtitle1}
  color: ${props => props.theme.colors.text.primary};
  text-align: right;
`;

const StyledValueGrid = styled(Grid)`
  padding-right: ${props => props.theme.spacing[2]};
`;

const Row = ({
  description,
  amount,
  showDivider,
  currency,
}) => (
  <>
    <StyledRow>
      <Grid container>
        <StyledDescriptionGrid item xs={8} lg={10}>
          <StyledDescription>{description}</StyledDescription>
        </StyledDescriptionGrid>
        <StyledValueGrid item xs={4} lg={2}>
          <Grid container justify="flex-end">
            <StyledValue>{formatCurrency(amount, currency)}</StyledValue>
          </Grid>
        </StyledValueGrid>
      </Grid>
    </StyledRow>
    {showDivider && <Divider />}
  </>
);

const SkillInvoiceSummary = ({
  skillInvoiceSummary: {
    skillName,
    numberOfTimesheets,
    ordinaryHours,
    baseHourlyRate,
    ordinaryWages,
    penaltyHours,
    penaltyWages,
    payrollComplianceFee,
    serviceFee,
    subtotal,
    currency,
  },
  onSkillInvoiceExpansion,
}) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <StyledExpansionPanel
      onChange={(_, isExpanded) => {
        setExpanded(isExpanded);
        onSkillInvoiceExpansion();
      }}
    >
      <StyledExpansionPanelSummary
        aria-controls="panel1bh-content"
        id="panel1bh-header"
        expandIcon={<ExpandMoreIcon />}
      >
        <StyledExpansionPanelSummaryTitle>
          <StyledTitle>{skillName}</StyledTitle>
          <StyledSubtitle>{`${numberOfTimesheets} Timesheet${numberOfTimesheets > 1 ? 's' : ''}`}</StyledSubtitle>
          { !expanded &&
            <Row
              description={`${skillName} Total`}
              amount={subtotal}
              currency={currency}
              showDivider={false}
            />
          }
        </StyledExpansionPanelSummaryTitle>
      </StyledExpansionPanelSummary>

      <StyledExpansionPanelDetails>
        <StyledSummaryWrapper>
          <Row
            description={`Ordinary Hours x ${ordinaryHours} @ ${formatCurrency(baseHourlyRate, currency)}ph`}
            amount={ordinaryWages}
            currency={currency}
          />
          <Row
            description={`Penalty Hours x ${penaltyHours}`}
            amount={penaltyWages}
            currency={currency}
          />
          <Row
            description="Payroll Compliance Fee (Super, Payroll Tax and Work Cover)"
            amount={payrollComplianceFee}
            currency={currency}
          />
          <Row
            description="Service Fee"
            amount={serviceFee}
            currency={currency}
          />
          <Row
            description={`${skillName} Total`}
            amount={subtotal}
            currency={currency}
            showDivider={false}
          />
        </StyledSummaryWrapper>
      </StyledExpansionPanelDetails>
    </StyledExpansionPanel>
  );
};

Row.propTypes = {
  description: PropTypes.string.isRequired,
  amount: PropTypes.number.isRequired,
  currency: PropTypes.string.isRequired,
  showDivider: PropTypes.bool,
};

Row.defaultProps = {
  showDivider: true,
};

SkillInvoiceSummary.propTypes = {
  skillInvoiceSummary: PropTypes.shape({
    skillName: PropTypes.string.isRequired,
    numberOfTimesheets: PropTypes.number.isRequired,
    ordinaryHours: PropTypes.number.isRequired,
    baseHourlyRate: PropTypes.number.isRequired,
    ordinaryWages: PropTypes.number.isRequired,
    penaltyHours: PropTypes.number.isRequired,
    penaltyWages: PropTypes.number.isRequired,
    payrollComplianceFee: PropTypes.number.isRequired,
    serviceFee: PropTypes.number.isRequired,
    subtotal: PropTypes.number.isRequired,
    currency: PropTypes.string.isRequired,
  }).isRequired,
  onSkillInvoiceExpansion: PropTypes.func,
};

SkillInvoiceSummary.defaultProps = {
  onSkillInvoiceExpansion: () => {},
};

export default SkillInvoiceSummary;
